import type { GQLLocale } from 'graphql-ts-types';

import type { GqlValidationRules } from 'common/components/DynamicForm/types';
import { ValidationRuleType } from 'common/components/DynamicForm/types';
import { executeQuery } from 'common/gql';
import type {
  PasswordHelpMessagesConfigFragment,
  RegistrationValidationRulesQuery,
  RegistrationValidationRulesQueryVariables,
} from 'common/gql/generated/schema';
import { combineQueryWithFragments } from 'common/gql/utils/fragments';
import validationRuleAttributes from 'common/hooks/validation-rules/gql/fragment//validationRuleAttributes.gql';
import validationRuleGroupAttributes from 'common/hooks/validation-rules/gql/fragment//validationRuleGroupAttributes.gql';
import baseValidationRuleAttributes from 'common/hooks/validation-rules/gql/fragment/baseValidationRuleAttributes.gql';
import passwordHelpValidationAttributes from 'common/hooks/validation-rules/gql/fragment/passwordHelpValidationAttributes.gql';
import { captureMessage } from 'common/utils/error-reporter';
import registrationValidationRulesQuery from '../../gql/query/validation-rules.gql';

export interface ValidationRules {
  firstname: GqlValidationRules;
  lastname: GqlValidationRules;
  email: GqlValidationRules;
  password: GqlValidationRules;
  companyName: GqlValidationRules;
  vatTaxNumber: GqlValidationRules;
  vatNumber: GqlValidationRules;
  passwordHelpMessagesConfig: PasswordHelpMessagesConfigFragment[];
}

export const fetchRegistrationRules = async (locale: GQLLocale) => {
  try {
    const { validationRules } = await executeQuery<
      RegistrationValidationRulesQuery,
      RegistrationValidationRulesQueryVariables
    >({
      query: combineQueryWithFragments(
        registrationValidationRulesQuery,
        baseValidationRuleAttributes,
        validationRuleAttributes,
        validationRuleGroupAttributes,
        passwordHelpValidationAttributes
      ),
      variables: { locale },
    });

    const transformedValidationRules = {
      ...validationRules,
      companyName: validationRules.company.name,
      vatNumber: validationRules.company.vatNumber,
      vatTaxNumber: [
        {
          ruleType: ValidationRuleType.AnyOf,
          rules: [
            {
              ruleType: ValidationRuleType.AllOf,
              rules: validationRules.company.vatNumber,
            },
            {
              ruleType: ValidationRuleType.AllOf,
              rules: validationRules.company.taxNumber,
            },
          ],
        },
      ],
    };

    return transformedValidationRules as ValidationRules;
  } catch (error) {
    captureMessage(`Failed to fetch the registration form validation rules. ${error}`);
  }
};
